/* eslint jsx-a11y/label-has-associated-control: ["error", { "assert": "nesting" }] */
/* eslint react/jsx-key: "off" */
// ScrollTable wraps each supplied cell in its own keyed table cell.
import { FormEvent, ReactNode, useEffect, useState } from "react";
import styled from "styled-components";
import { isAffiliateTestEnvironment } from "../../utils/env";
import { verifiedAffiliateEnvironment } from "../../hooks/api/admin/affiliateEnvironment";
import { LifecycleReviewPanel } from "./LifecycleReviewPanel";
import { ScenarioTab } from "./ScenarioTab";
import { RecoveryPanel } from "./RecoveryPanel";
import { ActivationPanel } from "./ActivationPanel";
import { ConfigurationPanel } from "./ConfigurationPanel";
import { TradeHistoryPanel } from "./TradeHistoryPanel";
import { currentAffiliateTier, LifecyclePanel } from "./LifecyclePanel";
import {
  AffiliateOperation,
  AffiliatePayoutPreview,
  AffiliateRates,
  AffiliateTier,
} from "../../interfaces/affiliate";
import {
  affiliateError,
  useAffiliates,
} from "../../hooks/api/admin/useAffiliates";
import {
  BACKGROUND_COLORS,
  BORDER_COLORS,
  TEXT_COLORS,
} from "../../constants/design/colors";

const Page = styled.main`
  height: calc(100vh - 110px);
  overflow: auto;
  padding: 24px;
  box-sizing: border-box;
  color: ${TEXT_COLORS.one};
  h1 {
    font-size: 24px;
    margin: 0 0 8px;
  }
  h2 {
    font-size: 17px;
    margin: 0 0 12px;
  }
  p {
    color: ${TEXT_COLORS.two};
    line-height: 1.5;
  }
  section {
    margin: 16px 0;
    padding: 18px;
    border: 1px solid ${BORDER_COLORS.one};
    border-radius: 8px;
    background: ${BACKGROUND_COLORS.component};
  }
  form {
    display: flex;
    flex-wrap: wrap;
    align-items: end;
    gap: 12px;
  }
  fieldset {
    border: 0;
    padding: 0;
    margin: 0;
    display: contents;
  }
  label {
    display: flex;
    flex-direction: column;
    gap: 6px;
    font-size: 12px;
    min-width: 140px;
  }
  input,
  select,
  textarea,
  button {
    font: inherit;
    border-radius: 4px;
    border: 1px solid ${BORDER_COLORS.two};
    padding: 8px;
    background: ${BACKGROUND_COLORS.eight};
    color: inherit;
  }
  input {
    min-width: 150px;
  }
  input[type="checkbox"] {
    min-width: auto;
  }
  textarea {
    min-width: min(440px, 70vw);
    min-height: 88px;
  }
  button {
    cursor: pointer;
    background: #152b45;
    min-height: 34px;
  }
  button:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
  table {
    border-collapse: collapse;
    width: 100%;
    font-size: 12px;
  }
  th,
  td {
    border-bottom: 1px solid ${BORDER_COLORS.one};
    padding: 10px;
    text-align: left;
    vertical-align: top;
  }
  th {
    color: ${TEXT_COLORS.two};
    white-space: nowrap;
  }
  code {
    font-size: 11px;
    overflow-wrap: anywhere;
  }
  pre {
    overflow: auto;
    font-size: 11px;
    max-height: 360px;
  }
  summary {
    cursor: pointer;
    padding: 8px 0;
  }
  [role="alert"] {
    padding: 12px;
    background: #411d2a;
    border-radius: 4px;
    margin: 12px 0;
  }
  [role="status"] {
    padding: 12px;
    background: #172b3d;
    border-radius: 4px;
    margin: 12px 0;
  }
`;

function ScrollTable({
  headers,
  rows,
  empty,
}: {
  headers: string[];
  rows: ReactNode[][];
  empty: string;
}) {
  return rows.length ? (
    <div style={{ overflowX: "auto" }}>
      <table>
        <thead>
          <tr>
            {headers.map((header) => (
              <th key={header}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => (
                <td key={j}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : (
    <p>{empty}</p>
  );
}

function Address({ value }: { value: string }) {
  return <code>{value}</code>;
}
const date = (value?: string) =>
  value ? new Date(value).toLocaleString() : "—";
const walletPattern = "0x[a-fA-F0-9]{40}";
const decimalPattern = "[0-9]+(\\.[0-9]{1,6})?";
const rateFields: { key: keyof AffiliateRates; label: string }[] = [
  { key: "perp_taker_bps", label: "Perp taker (bps)" },
  { key: "perp_maker_bps", label: "Perp maker (bps)" },
  { key: "option_taker_bps", label: "Options taker (bps)" },
  { key: "option_maker_bps", label: "Options maker (bps)" },
];
const defaultRates: AffiliateRates = {
  perp_taker_bps: "4",
  perp_maker_bps: "2",
  option_taker_bps: "4.5",
  option_maker_bps: "2.5",
};
const formValues = (event: FormEvent<HTMLFormElement>) => {
  event.preventDefault();
  return new FormData(event.currentTarget);
};
const field = (form: FormData, name: string) =>
  String(form.get(name) || "").trim();
const ratesFrom = (form: FormData): AffiliateRates => ({
  perp_taker_bps: field(form, "perp_taker_bps"),
  perp_maker_bps: field(form, "perp_maker_bps"),
  option_taker_bps: field(form, "option_taker_bps"),
  option_maker_bps: field(form, "option_maker_bps"),
});
function RateInputs({ rates = defaultRates }: { rates?: AffiliateRates }) {
  return (
    <>
      {rateFields.map(({ key, label }) => (
        <label key={key}>
          {label}
          <input
            name={key}
            required
            pattern={decimalPattern}
            inputMode="decimal"
            defaultValue={rates[key]}
          />
        </label>
      ))}
    </>
  );
}
function WalletInput({
  label = "Wallet",
  name = "wallet",
}: {
  label?: string;
  name?: string;
}) {
  return (
    <label>
      {label}
      <input
        name={name}
        required
        pattern={walletPattern}
        placeholder="0x…"
        autoComplete="off"
        style={{ width: 290 }}
      />
    </label>
  );
}

export function Affiliates() {
  const api = useAffiliates();
  const { data, command: affiliateCommand } = api;
  const testnet = isAffiliateTestEnvironment();
  const [localError, setLocalError] = useState<string>();
  const [preview, setPreview] = useState<AffiliatePayoutPreview>();
  const [refreshPayoutAffiliate, setRefreshPayoutAffiliate] =
    useState<string>();
  const [selectedRatesWallet, setSelectedRatesWallet] = useState("");
  const [tab, setTab] = useState(() =>
    testnet && new URLSearchParams(window.location.search).has("scenario")
      ? "scenarios"
      : "setup"
  );
  const verified = verifiedAffiliateEnvironment(data) && !api.error;
  const locked = api.busy || Boolean(api.retryCommand) || !verified;
  const disabled = locked || !data?.enabled;
  useEffect(() => {
    if (!refreshPayoutAffiliate || locked) return;
    setRefreshPayoutAffiliate(undefined);
    setPreview(undefined);
    affiliateCommand("preview_payout", { affiliate: refreshPayoutAffiliate })
      .then((result) => setPreview(result as AffiliatePayoutPreview))
      .catch((error) => setLocalError(affiliateError(error)));
  }, [refreshPayoutAffiliate, locked, affiliateCommand]);
  const previewBatch = data?.ledger?.batches?.find(
    (batch) => batch.request.id === preview?.payout_id
  );
  const hasEligibleCash =
    preview &&
    /^[0-9]+(\.[0-9]+)?$/.test(preview.cash_eligible_usd) &&
    /[1-9]/.test(preview.cash_eligible_usd);
  const cashPointsOnly = data?.reward_policy === "cash_points_v1";
  const showDaoPreview =
    !cashPointsOnly ||
    Number(preview?.dao_eligible_usd) > 0 ||
    Number(preview?.dao_held_usd) > 0;
  const featureStatus = data
    ? String(data.enabled ? "Enabled" : "Disabled")
    : "Loading status…";

  const run = async (
    operation: AffiliateOperation,
    payload: Record<string, unknown>
  ) => {
    setLocalError(undefined);
    if (operation === "preview_payout") setPreview(undefined);
    try {
      const result = await api.command(operation, payload);
      if (operation === "preview_payout")
        setPreview(result as AffiliatePayoutPreview);
      if (operation === "release_payout") {
        setPreview(undefined);
        if (typeof payload.affiliate === "string")
          setRefreshPayoutAffiliate(payload.affiliate);
      }
      return true;
    } catch (error) {
      setLocalError(affiliateError(error));
      return false;
    }
  };
  const submit =
    (
      operation: AffiliateOperation,
      build: (form: FormData) => Record<string, unknown>
    ) =>
    (event: FormEvent<HTMLFormElement>) => {
      const form = formValues(event);
      try {
        run(operation, build(form));
      } catch (error) {
        setLocalError(affiliateError(error));
      }
    };

  if (!api.allowed)
    return (
      <Page>
        <h1>Affiliates</h1>
        <p>
          Affiliate V2 controls are available only in testnet and local
          environments, or after explicit production enablement.
        </p>
      </Page>
    );

  return (
    <Page>
      <h1>
        Affiliates{" "}
        <span style={{ fontSize: 13, color: "#77bcff" }}>
          {testnet ? "TESTNET" : "MAINNET"}
        </span>
      </h1>
      <p>
        Approve affiliates, manage linked wallets, then inspect recorded fees
        and rewards.
      </p>
      {testnet && <RecoveryPanel verifiedTestnet={verified} />}
      {data && <ActivationPanel data={data} locked={locked} run={run} />}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          alignItems: "center",
        }}
      >
        <strong>{featureStatus}</strong>
        <span>Policy: {data?.policy_version || "—"}</span>
        <button
          type="button"
          onClick={() => {
            api.mutate();
          }}
          disabled={api.busy}
        >
          Refresh server state
        </button>
        <button
          type="button"
          disabled={!verified || api.busy}
          onClick={() => {
            if (!data || !verified) return;
            const url = URL.createObjectURL(
              new Blob([JSON.stringify(data, null, 2)], {
                type: "application/json",
              })
            );
            const link = document.createElement("a");
            link.href = url;
            link.download = `affiliate-testnet-state-${new Date()
              .toISOString()
              .replace(/[:.]/g, "-")}.json`;
            link.click();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
          }}
        >
          Download backend state
        </button>
      </div>
      {api.error && (
        <div role="alert">
          Cannot read affiliate state: {affiliateError(api.error)}. Changes are
          disabled.
        </div>
      )}
      {data && !verifiedAffiliateEnvironment(data) && (
        <div role="alert">
          The backend did not identify the expected environment. Changes are
          disabled.
        </div>
      )}
      {(localError || api.commandError) && (
        <div role="alert">{localError || api.commandError}</div>
      )}
      {api.receipt && (
        <div role="status">
          Command accepted: <code>{api.receipt.request_id}</code>. Tables show
          server state; acceptance alone does not confirm a completed payment.
        </div>
      )}
      {api.retryCommand && (
        <div role="status">
          The last request has an unconfirmed outcome. Retry uses the same
          request ID to prevent duplicate changes.{" "}
          <button
            type="button"
            disabled={api.busy}
            onClick={() => {
              setLocalError(undefined);
              api
                .retry()
                ?.then((result) => {
                  if ("request" in result)
                    setPreview(result as AffiliatePayoutPreview);
                  if (api.retryCommand?.operation === "release_payout") {
                    setPreview(undefined);
                    const { affiliate } = api.retryCommand.payload;
                    if (typeof affiliate === "string")
                      setRefreshPayoutAffiliate(affiliate);
                  }
                })
                ?.catch((error) => setLocalError(affiliateError(error)));
            }}
          >
            Retry last request
          </button>
        </div>
      )}
      {data?.decisions?.length ? (
        <section>
          <h2>Program rules and pending decisions</h2>
          <p>
            Confirmed rules are distinguished from provisional choices that
            still need Babbala’s answer before launch.
          </p>
          <ul>
            {data.decisions.map((decision) => (
              <li key={decision.key}>
                <code>{decision.key}</code>: {decision.value}{" "}
                {decision.provisional ? "(provisional)" : "(confirmed)"}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      <nav
        aria-label="Affiliate sections"
        style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 16 }}
      >
        {[
          ...(testnet ? [["scenarios", "Scenario lab"]] : []),
          ["setup", "Setup & wallets"],
          ["allowances", "Points & zero fees"],
          ["fills", "Trades & earnings"],
          ["payouts", "Payouts"],
        ].map(([key, label]) => (
          <button
            type="button"
            key={key}
            aria-pressed={tab === key}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </nav>

      {testnet && tab === "scenarios" && <ScenarioTab />}

      {tab === "setup" && (
        <>
          {testnet && data?.capabilities?.reset_testnet_cohort && (
            <section>
              <h2>Start a fresh two-wallet test</h2>
              <p>
                Archives this affiliate test run and clears its active referral
                setup, earnings, points and fee budgets. Existing exchange
                balances, positions and trade history stay unchanged. The
                backend rejects resets involving other wallets or payouts.
                Disable testnet access first. The program stays disabled after
                the reset.
              </p>
              <form
                onSubmit={submit("reset_testnet_cohort", (form) => ({
                  affiliate: field(form, "reset_affiliate"),
                  traders: [field(form, "reset_trader")],
                  expected_generation: data.reset_generation,
                  expected_state_digest: data.reset_state_digest,
                }))}
              >
                <fieldset
                  disabled={
                    locked ||
                    data.enabled ||
                    !Number.isInteger(data.reset_generation) ||
                    !/^[a-f0-9]{64}$/.test(data.reset_state_digest || "")
                  }
                >
                  <WalletInput
                    label="Affiliate to reset"
                    name="reset_affiliate"
                  />
                  <WalletInput label="Trader to reset" name="reset_trader" />
                  <label>
                    I want to archive this disposable test run
                    <input type="checkbox" required />
                  </label>
                  <button type="submit">Archive and reset test run</button>
                </fieldset>
              </form>
            </section>
          )}
          <ConfigurationPanel data={data} locked={locked} run={run} />
          <section>
            <h2>2. Affiliate approval</h2>
            <form
              onSubmit={(event) => {
                const form = formValues(event);
                run(field(form, "action") as "approve" | "upgrade", {
                  affiliate: field(form, "affiliate"),
                  tier: field(form, "tier") as AffiliateTier,
                });
              }}
            >
              <fieldset disabled={disabled}>
                <WalletInput label="Affiliate wallet" name="affiliate" />
                <label>
                  Tier
                  <select name="tier" defaultValue="VIP3">
                    <option>VIP1</option>
                    <option>VIP2</option>
                    <option>VIP3</option>
                  </select>
                </label>
                <label>
                  Action
                  <select name="action">
                    <option value="approve">Approve new affiliate</option>
                    <option value="upgrade">Upgrade existing affiliate</option>
                  </select>
                </label>
                <button type="submit">Apply affiliate tier</button>
              </fieldset>
            </form>
            <ScrollTable
              headers={["Affiliate", "Tier", "Approved"]}
              rows={(data?.affiliates || []).map((affiliate) => [
                <Address value={affiliate.address} />,
                currentAffiliateTier(
                  affiliate,
                  Date.parse(data?.ledger.evaluation_at || "")
                ) || "Awaiting backend time",
                date(affiliate.approved_at),
              ])}
              empty="No approved affiliates."
            />
          </section>
          {data && (
            <LifecyclePanel data={data} disabled={disabled} onCommand={run} />
          )}
          <section>
            <h2>3. Create a code</h2>
            <form
              onSubmit={submit("create_code", (form) => ({
                affiliate: field(form, "affiliate"),
                code: field(form, "code"),
                rates: ratesFrom(form),
                use_cap: Number(field(form, "use_cap")),
                expires_at: new Date(field(form, "expires_at")).toISOString(),
              }))}
            >
              <fieldset disabled={disabled}>
                <WalletInput label="Code owner" name="affiliate" />
                <label>
                  Code
                  <input name="code" required autoComplete="off" />
                </label>
                <RateInputs />
                <label>
                  Use cap
                  <input
                    type="number"
                    min="1"
                    step="1"
                    max={Number.MAX_SAFE_INTEGER}
                    name="use_cap"
                    required
                    defaultValue="10"
                  />
                </label>
                <label>
                  Code expires (local time)
                  <input type="datetime-local" name="expires_at" required />
                </label>
                <button type="submit">Create affiliate code</button>
              </fieldset>
            </form>
            <ScrollTable
              headers={[
                "Code",
                "Owner",
                "Uses / cap",
                "Expires",
                "Perp taker / maker",
                "Options taker / maker",
              ]}
              rows={(data?.codes || []).map((code) => [
                code.code,
                <Address value={code.affiliate} />,
                `${code.uses} / ${code.use_cap}`,
                date(code.expires_at),
                `${code.rates.perp_taker_bps} / ${code.rates.perp_maker_bps} bps`,
                `${code.rates.option_taker_bps} / ${code.rates.option_maker_bps} bps`,
              ])}
              empty="No affiliate codes."
            />
          </section>
          <section>
            <h2>4. Attach a test wallet</h2>
            <p>
              Attachment is permanent. Traders can also enter the code on their
              testnet affiliate page.
            </p>
            <form
              onSubmit={submit("attach", (form) => ({
                wallet: field(form, "wallet"),
                code: field(form, "code"),
              }))}
            >
              <fieldset disabled={disabled}>
                <WalletInput label="Trader wallet" />
                <label>
                  Affiliate code
                  <input name="code" required />
                </label>
                <button type="submit">Attach test wallet permanently</button>
              </fieldset>
            </form>
            <ScrollTable
              headers={[
                "Wallet",
                "Affiliate / code",
                "Attached / cashback stops",
                "Perp taker / maker",
                "Options taker / maker",
                "Remaining zero-fee budget",
              ]}
              rows={(data?.attachments || []).map((account) => [
                <Address value={account.wallet} />,
                <>
                  <Address value={account.affiliate} />
                  <br />
                  {account.code}
                </>,
                <>
                  {date(account.attached_at)}
                  <br />
                  {date(account.cashback_cutoff)}
                </>,
                `${account.rates.perp_taker_bps} / ${account.rates.perp_maker_bps} bps`,
                `${account.rates.option_taker_bps} / ${account.rates.option_maker_bps} bps`,
                `$${account.allowance_remaining_usd}`,
              ])}
              empty="No attached wallets."
            />
          </section>
          <section>
            <h2>5. Change wallet fees</h2>
            <p>
              Reductions apply immediately; increases take effect after seven
              days. Any actual fee edit locks further changes to that wallet for
              seven days. The backend enforces the lock for all fee components.
            </p>
            <form
              key={selectedRatesWallet}
              onSubmit={submit("set_rates", (form) => ({
                wallet: field(form, "wallet"),
                rates: ratesFrom(form),
              }))}
            >
              <fieldset disabled={disabled}>
                <label>
                  Attached wallet
                  <select
                    name="wallet"
                    required
                    value={selectedRatesWallet}
                    onChange={(event) =>
                      setSelectedRatesWallet(event.target.value)
                    }
                  >
                    <option value="">Choose a wallet</option>
                    {data?.attachments?.map((account) => (
                      <option key={account.wallet} value={account.wallet}>
                        {account.wallet}
                      </option>
                    ))}
                  </select>
                </label>
                <RateInputs
                  rates={
                    data?.attachments?.find(
                      (account) => account.wallet === selectedRatesWallet
                    )?.rates
                  }
                />
                <button type="submit">Schedule wallet fees</button>
              </fieldset>
            </form>
            <ScrollTable
              headers={["Wallet", "Fee", "Current", "Next", "Effective"]}
              rows={(data?.attachments || []).flatMap((account) =>
                (
                  [
                    "perp_taker",
                    "perp_maker",
                    "option_taker",
                    "option_maker",
                  ] as const
                )
                  .filter((key) => account[key]?.next_rate_bps !== undefined)
                  .map((key) => [
                    <Address value={account.wallet} />,
                    key.replaceAll("_", " "),
                    `${account[key].rate_bps} bps`,
                    `${account[key].next_rate_bps} bps`,
                    date(account[key].next_effective_at),
                  ])
              )}
              empty="No pending fee changes."
            />
          </section>
        </>
      )}

      {tab === "allowances" && (
        <>
          <section>
            <h2>Grant welcome points</h2>
            <p>
              Welcome points are available immediately. Earned points vest after
              seven days. Four available points fund $1 in waived trading fees.
            </p>
            <form
              onSubmit={submit("grant_welcome", (form) => ({
                affiliate: field(form, "affiliate"),
                points: field(form, "points"),
              }))}
            >
              <fieldset disabled={disabled}>
                <WalletInput label="Grant recipient" name="affiliate" />
                <label>
                  Welcome points
                  <input
                    name="points"
                    required
                    pattern={decimalPattern}
                    inputMode="decimal"
                  />
                </label>
                <button type="submit">Grant welcome points</button>
              </fieldset>
            </form>
            <ScrollTable
              headers={[
                "Affiliate",
                "Origin",
                "Remaining points",
                "Available",
                "Expires",
              ]}
              rows={(data?.ledger?.points || []).map((lot) => [
                <Address value={lot.affiliate_id} />,
                lot.kind,
                lot.remaining,
                date(lot.available_at),
                date(lot.expires_at),
              ])}
              empty="No point lots. Trade through the exchange to earn points, or grant welcome points above."
            />
          </section>
          <section>
            <h2>Fund zero-fee trading</h2>
            <p>
              The backend verifies available points and ownership. Wallet age
              does not affect welcome-point eligibility. Leave the end time
              blank for a budget that lasts until spent.
            </p>
            <form
              onSubmit={submit("allocate_allowance", (form) => ({
                affiliate: field(form, "affiliate"),
                wallet: field(form, "wallet"),
                dollars: field(form, "dollars"),
                ...(field(form, "end_at")
                  ? { end_at: new Date(field(form, "end_at")).toISOString() }
                  : {}),
              }))}
            >
              <fieldset disabled={disabled}>
                <WalletInput label="Funding affiliate" name="affiliate" />
                <WalletInput label="Zero-fee wallet" />
                <label>
                  Zero-fee budget (USD)
                  <input
                    name="dollars"
                    required
                    pattern={decimalPattern}
                    inputMode="decimal"
                  />
                </label>
                <label>
                  Optional end (local time)
                  <input name="end_at" type="datetime-local" />
                </label>
                <button type="submit">Fund zero-fee budget</button>
              </fieldset>
            </form>
            <ScrollTable
              headers={["Budget", "Wallet", "Funded", "Remaining", "Ends"]}
              rows={(data?.ledger?.allowances || []).map((allowance) => [
                allowance.id,
                <Address value={allowance.wallet} />,
                `$${allowance.original_usd}`,
                `$${allowance.remaining_usd}`,
                allowance.end_at ? date(allowance.end_at) : "Until spent",
              ])}
              empty="No funded zero-fee budgets."
            />
          </section>
        </>
      )}

      {tab === "fills" && (
        <TradeHistoryPanel state={verified ? data : undefined} />
      )}

      {tab === "payouts" && (
        <>
          {verified && data && (
            <LifecycleReviewPanel
              data={data}
              disabled={locked}
              command={api.command}
            />
          )}
          <section>
            <h2>Preview and release {testnet ? "testnet " : ""}USDC</h2>
            <p>
              Release eligible cash commission to the affiliate’s internal Aevo
              USDC balance. Cash stays on hold for seven days; this page cannot
              accelerate the holdback.
              {cashPointsOnly
                ? " New DAO AEVO rewards and AEVO payouts are disabled. Historical DAO entitlements remain recorded and unpaid."
                : " DAO AEVO entitlement remains accrued and unpaid until its payment network and rules are agreed."}
            </p>
            <form
              onSubmit={submit("preview_payout", (form) => ({
                affiliate: field(form, "affiliate"),
              }))}
            >
              <fieldset disabled={disabled}>
                <WalletInput label="Payout affiliate" name="affiliate" />
                <button type="submit">Preview payout</button>
              </fieldset>
            </form>
            {preview && (
              <div>
                <h3>
                  Preview for <Address value={preview.request.affiliate_id} />
                </h3>
                <p>Cutoff: {date(preview.request.cutoff)}</p>
                <ScrollTable
                  headers={[
                    "Eligible USDC",
                    "USDC under holdback",
                    ...(showDaoPreview
                      ? [
                          cashPointsOnly
                            ? "Historical DAO value (unpaid)"
                            : "Accrued DAO value (unpaid)",
                          "Held DAO value (unpaid)",
                          "AEVO quantity (not released)",
                        ]
                      : []),
                  ]}
                  rows={[
                    [
                      `$${preview.cash_eligible_usd}`,
                      `$${preview.cash_held_usd}`,
                      ...(showDaoPreview
                        ? [
                            `$${preview.dao_eligible_usd}`,
                            `$${preview.dao_held_usd}`,
                            `${preview.dao_tokens}${
                              preview.token_quantity_estimate
                                ? " (estimate)"
                                : ""
                            }`,
                          ]
                        : []),
                    ],
                  ]}
                  empty=""
                />
                {(preview.decision_needed || []).map((decision) => (
                  <p key={decision}>{decision}</p>
                ))}
                {preview.explicit_payout_hold && <p>Payout is on hold.</p>}
                <p>
                  Destination: the internal Aevo balance of{" "}
                  <Address value={preview.request.affiliate_id} />. This
                  releases USDC only.
                </p>
                {!hasEligibleCash && (
                  <p>No USDC is eligible for release at this cutoff.</p>
                )}
                {previewBatch && (
                  <p>
                    This payout already has a backend batch. Its status is shown
                    below. Resume an unpaid transfer from that existing batch.
                  </p>
                )}
                <button
                  type="button"
                  disabled={
                    disabled ||
                    !data?.capabilities?.release_payout ||
                    !preview.payout_id ||
                    preview.explicit_payout_hold ||
                    !hasEligibleCash ||
                    Boolean(previewBatch)
                  }
                  onClick={() => {
                    if (preview.payout_id)
                      run("release_payout", {
                        affiliate: preview.request.affiliate_id,
                        payout_id: preview.payout_id,
                      });
                  }}
                >
                  Release {testnet ? "testnet " : ""}USDC
                </button>
              </div>
            )}
            {!data?.capabilities?.release_payout && (
              <p>
                Payment release is unavailable on this backend. A preview does
                not move funds.
              </p>
            )}
          </section>
          <section>
            <h2>Payment status from the backend</h2>
            <ScrollTable
              headers={[
                "Batch",
                "Affiliate",
                "USDC",
                "DAO value / AEVO in batch",
                "Status",
                "Payment reference",
                "Paid at",
                "Resume existing transfer",
              ]}
              rows={(data?.ledger?.batches || []).map((batch) => [
                batch.request.id,
                <Address value={batch.request.affiliate_id} />,
                `$${batch.cash_usd}`,
                `$${batch.dao_usd} / ${batch.dao_tokens}`,
                batch.status === "reserved" &&
                data?.cash_payments?.[batch.request.id]?.submitted
                  ? "submitted"
                  : batch.status,
                batch.payment_id || "—",
                date(batch.paid_at),
                <button
                  type="button"
                  aria-label={`Resume USDC transfer ${batch.request.id}`}
                  disabled={
                    disabled ||
                    !data?.capabilities?.release_payout ||
                    batch.status === "paid" ||
                    !/^[0-9]+(\.[0-9]+)?$/.test(batch.cash_usd) ||
                    !/[1-9]/.test(batch.cash_usd)
                  }
                  onClick={() =>
                    run("release_payout", {
                      affiliate: batch.request.affiliate_id,
                      payout_id: batch.request.id,
                    })
                  }
                >
                  {batch.status === "paid" ? "Paid" : "Resume USDC transfer"}
                </button>,
              ])}
              empty="No payout batches. Payments are not marked completed when you submit an approval."
            />
            <p>
              Resume reuses the existing payout ID and the backend’s signed
              transfer. It does not create a new entitlement or pay the batch
              twice.
            </p>
          </section>
        </>
      )}
      {verified && data && (
        <section>
          <details>
            <summary>Inspect complete backend state</summary>
            <pre>{JSON.stringify(data, null, 2)}</pre>
          </details>
        </section>
      )}
    </Page>
  );
}
